/**
 * Simulation or live — the one switch that decides whether the engine
 * writes anything to the PMS.
 *
 * Big, worded, and above the tabs, because until 2026-10-01 it lived in
 * a database column nobody could see from the app. Two states with
 * sentences, not a toggle with an icon: "nothing is sent" has to be
 * readable from across the room (Curro).
 *
 * Going live asks first and shows the exact pricelists that will be
 * written — every one a rate card writes to. Coming back to simulation
 * is immediate: turning something off never needs permission.
 */
import React, { useEffect, useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'
import {
	AppBulkEditAction, AppDialog, AppDialogBody, AppDialogCancel, AppDialogFooter, AppDialogHeader,
	AppNote, AppValueChip,
} from '@components'
import { dialogBodySx } from 'Pages/DsRepricing/@components/dialogBodySx'
import { dsColorNew } from '@palette'
import useToast from '@hooks/useToast'
import { setupApi } from 'Pages/DsRepricing/Setup/@services/api'

const T = { title: 15, body: 14, label: 13, caption: 12 }

export function useRepricingMode() {
	const [state, setState] = useState({ mode: null, enabled: null, targets: [], loading: true, error: null })
	// A failed load is not "no targets". Saying "no rate card writes" when
	// the request 404'd (an old webback) sent Curro to check a rate card
	// that was fine (2026-10-01). The error is kept and shown as such.
	const load = () => setupApi.mode.get()
		.then(r => setState({ mode: r.mode, enabled: r.enabled, targets: r.write_targets || [], loading: false, error: null }))
		.catch(e => setState(s => ({ ...s, loading: false,
		                              error: e?.status === 404 ? 'The server does not know the mode endpoint yet — is webback up to date?'
		                                   : (e?.message || 'Could not load the repricing mode') })))
	useEffect(() => { load() }, [])
	return [state, setState, load]
}

export default function ModeSwitch() {
	const toast = useToast()
	const [state, setState] = useRepricingMode()
	const [confirming, setConfirming] = useState(false)
	const [saving, setSaving] = useState(false)
	const live = state.mode === 'live'

	const apply = async (mode) => {
		setSaving(true)
		try {
			const r = await setupApi.mode.set(mode)
			setState(s => ({ ...s, mode: r.mode, enabled: r.enabled, targets: r.write_targets || [] }))
			toast.success(mode === 'live' ? 'Live: prices are now written to the PMS.'
			                              : 'Simulation: nothing is sent.')
		} catch (e) {
			toast.error(e?.message || 'Could not change the mode')
		} finally {
			setSaving(false); setConfirming(false)
		}
	}

	const option = (key, icon, title, text) => {
		const on = (key === 'live') === live
		// Live is the one that writes, so it is the one in red when chosen
		const tone = key === 'live'
			? { color: dsColorNew.dangerRed, bg: dsColorNew.dangerBg }
			: { color: dsColorNew.appBlue, bg: dsColorNew.pillActiveBg }
		return (
			<Box component="button" type="button" disabled={saving || state.loading}
			     onClick={() => { if (on) return; key === 'live' ? setConfirming(true) : apply('dry_run') }}
			     aria-pressed={on}
			     sx={{
				     appearance: 'none', cursor: on ? 'default' : 'pointer', textAlign: 'left',
				     display: 'flex', gap: 1.25, alignItems: 'flex-start',
				     px: 2, py: 1.5, borderRadius: '12px', minWidth: 300, flex: 1,
				     fontFamily: 'inherit',
				     border: '1px solid',
				     borderColor: on ? tone.color : dsColorNew.grayBorder,
				     bgcolor: dsColorNew.white,
				     boxShadow: on ? `0 0 0 3px ${tone.bg}` : 'none',
				     transition: 'box-shadow .18s, border-color .18s',
				     '&:hover': { borderColor: on ? undefined : dsColorNew.textSubtle },
			     }}>
				<Box sx={{ width: 32, height: 32, borderRadius: '8px', flexShrink: 0,
				           display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
				           bgcolor: on ? tone.bg : dsColorNew.pillTrackBg,
				           color: on ? tone.color : dsColorNew.textSubtle }}>
					<IconSeeker icon={icon} sx={{ fontSize: 13 }}/>
				</Box>
				<Box>
					<Typography sx={{ fontSize: 14, fontWeight: 600, letterSpacing: '0.02em',
					                  color: on ? dsColorNew.textTitle : dsColorNew.textMuted,
					                  display: 'flex', alignItems: 'center' }}>
						{title}{on && (
							<AppValueChip sx={{ ml: 1, py: 0.25, fontSize: 11, fontWeight: 600,
							                    bgcolor: tone.bg, color: tone.color }}>
								current
							</AppValueChip>
						)}
					</Typography>
					<Typography sx={{ fontSize: 12.5, color: dsColorNew.textMuted, lineHeight: 1.45, mt: 0.25 }}>
						{text}
					</Typography>
				</Box>
			</Box>
		)
	}

	return (
		<>
			{state.error && <AppNote severity="error" sx={{ mb: 1.5 }}>{state.error}</AppNote>}
			<Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} sx={{ mb: 2.5 }}>
				{option('dry_run', 'flask', 'SIMULATION',
				        'Nothing is sent to the PMS. Every decision is computed in full and recorded in the Activity log for review.')}
				{option('live', 'bolt', 'LIVE',
				        `Prices are written to the pricelists the rate cards point at${state.targets.length ? ` — ${state.targets.length} today` : ''}.`)}
			</Stack>

			<AppDialog open={confirming} onClose={() => !saving && setConfirming(false)} maxWidth="sm">
				<AppDialogHeader icon="bolt" title="Go live?" onClose={() => !saving && setConfirming(false)}/>
				<AppDialogBody sx={dialogBodySx}>
					<Typography sx={{ fontSize: T.body, color: dsColorNew.textDark, mb: 1.5 }}>
						From the next run, the engine will write real prices to these pricelists in the PMS:
					</Typography>
					{state.error ? (
						<AppNote severity="error">{state.error}</AppNote>
					) : state.targets.length === 0 ? (
						<AppNote severity="warning">
							No rate card writes to any pricelist yet. Live would change nothing —
							wire a rate card first.
						</AppNote>
					) : (
						<Stack gap={0.75}>
							{state.targets.map(t => (
								<Box key={t.pricelist_id} sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline',
								                                 px: 1.5, py: 1, border: `1px solid ${dsColorNew.grayBorder}`,
								                                 borderRadius: '10px' }}>
									<Typography sx={{ fontFamily: 'inherit', fontWeight: 600, fontSize: 13.5,
									                  color: dsColorNew.textTitle }}>
										{t.code || t.pricelist_id}
									</Typography>
									<Typography sx={{ fontSize: T.label, color: dsColorNew.textMuted }}>
										{t.name || 'not in the catalogue'}{t.source_id ? ` · PMS id ${t.source_id}` : ''}
									</Typography>
								</Box>
							))}
						</Stack>
					)}
					<Typography sx={{ fontSize: T.caption, color: dsColorNew.textMuted, mt: 2 }}>
						This is recorded in the audit trail with your name. You can come back to
						simulation at any time, without confirmation.
					</Typography>
				</AppDialogBody>
				<AppDialogFooter>
					<AppDialogCancel onClick={() => setConfirming(false)} disabled={saving}>Stay in simulation</AppDialogCancel>
					<AppBulkEditAction onClick={() => apply('live')}
					                   disabled={saving || !!state.error || state.targets.length === 0}
					                   sx={{ bgcolor: dsColorNew.dangerRed, '&:hover': { bgcolor: dsColorNew.dangerRed, filter: 'brightness(0.92)' } }}>
						Go live
					</AppBulkEditAction>
				</AppDialogFooter>
			</AppDialog>
		</>
	)
}

/** The one-line banner other screens show, so a log is never read in the wrong mode. */
export function ModeBanner() {
	const [state] = useRepricingMode()
	if (state.loading || !state.mode) return null
	const live = state.mode === 'live'
	return (
		<AppNote severity={live ? 'error' : 'info'} sx={{ mb: 2, fontWeight: 600 }}>
			{live ? 'LIVE — prices in this log were written to the PMS.'
			      : 'SIMULATION — nothing in this log was sent. Every price here is a proposal.'}
		</AppNote>
	)
}
